import { useState, useEffect } from 'react';
import { API } from '../App';

/**
 * Custom hook to fetch page content from the database
 */
export const usePageContent = (page, section = null) => {
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        setLoading(true);
        const url = section 
          ? `${API}/page-content/${page}/${section}`
          : `${API}/page-content/${page}`;
        
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error('Failed to fetch content');
        }
        
        const data = await response.json();
        setContent(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching page content:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (page) {
      fetchContent();
    }
  }, [page, section]);

  // Helper to get specific content by key/type
  const getContent = (key) => {
    const item = content.find(c => c.type === key || c.key === key);
    return item ? item.content : '';
  };

  return { content, loading, error, getContent };
};

/**
 * Hook to fetch programs
 */
export const usePrograms = () => {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        setLoading(true);
  const response = await fetch(`${API}/programs`);
        if (!response.ok) {
          throw new Error('Failed to fetch programs');
        }
        
        const data = await response.json();
        setPrograms(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching programs:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPrograms();
  }, []);

  return { programs, loading, error };
};

/**
 * Hook to fetch features (Why Choose Us)
 */
export const useFeatures = () => {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFeatures = async () => {
      try {
        setLoading(true);
  const response = await fetch(`${API}/features`);
        if (!response.ok) {
          throw new Error('Failed to fetch features');
        }
        
        const data = await response.json();
        setFeatures(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching features:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchFeatures();
  }, []);

  return { features, loading, error };
};

/**
 * Hook to fetch differentiators (What Makes Us Different)
 */
export const useDifferentiators = () => {
  const [differentiators, setDifferentiators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDifferentiators = async () => {
      try {
        setLoading(true);
  const response = await fetch(`${API}/differentiators`);
        if (!response.ok) {
          throw new Error('Failed to fetch differentiators');
        }
        
        const data = await response.json();
        setDifferentiators(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching differentiators:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDifferentiators();
  }, []);

  return { differentiators, loading, error };
};
